import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";

/**
 * Bloque 3 Partner Points — edición admin del perfil comercial.
 * Usa el endpoint dedicado /api/partner-profile/{id} (NUNCA el PUT de
 * empleados, que resetea el perfil RBAC oficial).
 */
const FIELDS = [
  ["business_name", "Nombre comercial *"],
  ["address", "Dirección"],
  ["city", "Ciudad"],
  ["state", "Estado"],
  ["zip", "ZIP Code"],
  ["phone", "Teléfono comercial"],
  ["contact_name", "Responsable"],
  ["partner_code", "Identificador Partner"],
  ["pickup_address", "Dirección de recogida"],
  ["commission_rate", "Comisión directa (%)"],
];

export default function PartnerProfileModal({ employee, onClose }) {
  const [form, setForm] = useState({ active: true, commission_rate: 18 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/partner-profile/${employee.id}`, { withCredentials: true })
      .then(({ data }) => setForm({ active: true, commission_rate: 18, ...(data.profile || {}) }))
      .catch(() => toast.error("No se pudo cargar el perfil"))
      .finally(() => setLoading(false));
  }, [employee.id]);

  const save = async () => {
    if (!(form.business_name || "").trim()) {
      toast.error("El nombre comercial es obligatorio");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/partner-profile/${employee.id}`, form, {
        withCredentials: true,
      });
      toast.success("Perfil Partner guardado");
      onClose();
    } catch {
      toast.error("Error al guardar el perfil");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="partner-profile-overlay"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        data-testid="partner-profile-modal"
        className="bg-white w-full max-w-lg max-h-[92vh] overflow-auto border border-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-black">
          <div className="label-overline">PARTNER POINT</div>
          <h3 className="font-display text-xl font-bold tracking-tight">
            Perfil comercial — {employee.name}
          </h3>
        </div>
        <div className="p-6 space-y-3">
          {loading ? (
            <div className="font-mono-tech text-sm text-center py-8">LOADING…</div>
          ) : (
            <>
              {FIELDS.map(([k, label]) => (
                <div key={k}>
                  <label className="font-mono-tech text-[10px] tracking-[0.15em] uppercase text-[var(--text-secondary)]">
                    {label}
                  </label>
                  <input
                    data-testid={`partner-profile-${k}`}
                    value={form[k] || ""}
                    onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                    className="w-full border border-[var(--border)] px-3 py-2 text-sm focus:border-black outline-none"
                  />
                </div>
              ))}
              <label className="flex items-center gap-2 pt-1 text-sm">
                <input
                  data-testid="partner-profile-active"
                  type="checkbox"
                  checked={form.active !== false}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                />
                Activo (puede crear y modificar ventas)
              </label>
            </>
          )}
        </div>
        <div className="px-6 py-4 border-t border-black flex justify-end gap-2">
          <button
            data-testid="partner-profile-cancel"
            onClick={onClose}
            className="border border-[var(--border)] px-4 py-2 text-sm hover:bg-[var(--surface-alt)]"
          >
            Cancelar
          </button>
          <button
            data-testid="partner-profile-save"
            onClick={save}
            disabled={saving || loading}
            className="bg-black text-white px-5 py-2 text-sm font-bold disabled:opacity-50"
          >
            {saving ? "Guardando…" : "Guardar perfil"}
          </button>
        </div>
      </div>
    </div>
  );
}
